import find from 'lodash/find';
import { matchPath } from 'react-router-dom';
import { compile } from 'path-to-regexp';
import { stringify } from './urlHelpers';
import { DEFAULT_LOCALE, getActiveLocale, isSupportedLocale, localeFromPathname } from './locale';
// NOTE: This file imports urlHelpers.js, which may lead to circular dependency

const findRouteByName = (nameToFind, routes) => find(routes, route => route.name === nameToFind);

/**
 * E.g. ```const toListingPath = toPathByRouteName('ListingPage', routes);```
 * Then we can generate listing paths with given params (```toListingPath({ id: uuidX })```)
 */
const toPathByRouteName = (nameToFind, routes) => {
  const route = findRouteByName(nameToFind, routes);
  if (!route) {
    throw new Error(`Path "${nameToFind}" was not found.`);
  }
  return compile(route.path);
};

const withLocaleParam = params => {
  // Prefer an explicit param, then the current URL segment, then the in-memory
  // active locale. URL beats memory so navigations that haven't flushed
  // LocaleAwareProviders' useEffect yet still generate correct links (e.g. EN
  // listing → checkout while profile preference is Arabic).
  const fromPath =
    typeof window !== 'undefined' ? localeFromPathname(window.location?.pathname) : null;
  const locale =
    (params && isSupportedLocale(params.locale) && params.locale) ||
    fromPath ||
    getActiveLocale() ||
    DEFAULT_LOCALE;
  return { ...params, locale };
};

/**
 * Shorthand for single path call. (```pathByRouteName('ListingPage', routes, { id: uuidX });```)
 */
export const pathByRouteName = (nameToFind, routes, params = {}) => {
  const hasEmptySlug = params && params.hasOwnProperty('slug') && params.slug === '';
  const pathParams = withLocaleParam(hasEmptySlug ? { ...params, slug: 'no-slug' } : params);
  return toPathByRouteName(nameToFind, routes)(pathParams);
};

/**
 * Find the matching routes and their params for the given pathname
 *
 * @param {String} pathname - Full URL path from root with possible
 * search params and hash included
 *
 * @return {Array<{ route, params }>} - All matches as { route, params } objects if matches has
 * exact flag set to false. If not, an array containing just the first matched exact route is returned.
 */
export const matchPathname = (pathname, routeConfiguration) => {
  const matchedRoutes = routeConfiguration.reduce((matches, route) => {
    const { path, exact = true } = route;
    const match = matchPath(pathname, { path, exact });
    if (match) {
      matches.push({
        route,
        params: match.params || {},
      });
    }
    return matches;
  }, []);

  const matchedExactRoute = matchedRoutes.find(r => {
    return r.exact === true || r.exact == null;
  });

  // We return matched 'exact' path route only if such exists
  // and all matches if no exact flag exists.
  return matchedExactRoute ? [matchedExactRoute] : matchedRoutes;
};

/**
 * ResourceLocatorString is used to direct webapp to correct page.
 * In contrast to Universal Resource Locator (URL), this doesn't contain protocol, host, or port.
 */
export const createResourceLocatorString = (
  routeName,
  routes,
  pathParams = {},
  searchParams = {},
  hash = ''
) => {
  const searchQuery = stringify(searchParams);
  const includeSearchQuery = searchQuery.length > 0 ? `?${searchQuery}` : '';
  const path = pathByRouteName(routeName, routes, pathParams);
  return `${path}${includeSearchQuery}${hash}`;
};

/**
 * Find component related to route name
 * E.g. `const PageComponent = findComponentByRouteName('CheckoutPage', routes);`
 * Then we can call static methods of given component:
 * `dispatch(PageComponent.setInitialValues({ listing, bookingDates }));`
 *
 * @param {String} nameToFind - Route name
 * @param {Array<{ route }>} routes - Route configuration as flat array.
 *
 * @return {Route} - Route that matches the given route name.
 */
export const findRouteByRouteName = (nameToFind, routes) => {
  const route = findRouteByName(nameToFind, routes);
  if (!route) {
    throw new Error(`Component "${nameToFind}" was not found.`);
  }
  return route;
};

/**
 * Get the canonical URL from the given location
 *
 * @param {Array<{ route }>} routes - Route configuration as flat array
 * @param {Object} location - location object from React Router
 *
 * @return {String} Canonical URL of the given location
 *
 */
export const canonicalRoutePath = (routes, location, pathOnly = false) => {
  const { pathname, search, hash } = location;

  const matches = matchPathname(pathname, routes);
  const isListingRoute = matches.length === 1 && matches[0].route.name === 'ListingPage';

  if (isListingRoute) {
    // Remove the dynamic slug from the listing page canonical URL
    // Paths are /:locale/l/:slug/:id
    const cleanedPathName = pathname.replace(/\/$/, '');
    const parts = cleanedPathName.split('/');
    const locale = localeFromPathname(pathname) || matches[0]?.params?.locale || DEFAULT_LOCALE;

    // ['', locale, 'l', slug, id]
    if (parts.length !== 5) {
      throw new Error('Expected ListingPage route to have 5 parts (including locale)');
    }
    const canonicalListingPathname = `/${locale}/${parts[2]}/${parts[4]}`;
    return pathOnly ? canonicalListingPathname : `${canonicalListingPathname}${search}${hash}`;
  }

  return pathOnly ? pathname : `${pathname}${search}${hash}`;
};

/**
 * Custom code for set/get timeslot start/end params from URL
 */
const SEARCH_PARAMS = {
  TIMESLOT_START: 'timeslotStart',
  TIMESLOT_END: 'timeslotEnd',
};

const SearchParams = (start, end) => ({
  [SEARCH_PARAMS.TIMESLOT_START]: start,
  [SEARCH_PARAMS.TIMESLOT_END]: end,
});

const validateAndParseTimeslot = (start, end) => {
  const parseDate = dateString => {
    const date = new Date(dateString);
    return !isNaN(date.getTime()) ? date : null;
  };

  const startDate = parseDate(start);
  const endDate = parseDate(end);
  return startDate && endDate ? SearchParams(startDate, endDate) : null;
};

export const useSearchParams = location => {
  const queryParams = location?.search ? new URLSearchParams(location.search) : null;
  if (!queryParams) return SearchParams(null, null);

  const start = queryParams.get(SEARCH_PARAMS.TIMESLOT_START);
  const end = queryParams.get(SEARCH_PARAMS.TIMESLOT_END);

  if (!start || !end) return SearchParams(null, null);

  return validateAndParseTimeslot(start, end) || SearchParams(null, null);
};

/**
 *
 * @param {{start, end}} dateParams
 * @return {{search: string}|null}
 */
export const toSearchParams = dateParams => {
  if (!dateParams) return null;

  const { start, end } = dateParams;
  const validTimeslot = validateAndParseTimeslot(start, end);

  if (!validTimeslot) return null;

  const params = new URLSearchParams();
  params.append(SEARCH_PARAMS.TIMESLOT_START, validTimeslot.timeslotStart.toISOString());
  params.append(SEARCH_PARAMS.TIMESLOT_END, validTimeslot.timeslotEnd.toISOString());

  return { search: `?${params.toString()}` };
};
